import "./FundOverview.scss";
import { useTranslation } from "react-i18next";
import moment from "moment";
import numeral from "numeral";
import { MdEmail, MdOutlineNumbers, MdPhone } from "react-icons/md";
import { CiImageOn } from "react-icons/ci";
import {
    Table,
    TablePagination,
    TableContainer,
    TableBody,
    TableRow,
    TableCell,
    Paper,
} from "@mui/material";
import { formatAmountsToShort } from "../../utils/genericFunctions";
import ExpandableComponent from "../expandableComponent/ExpandableComponent";
import { useState } from "react";


const FundOverview = ({ fundData }) => {
    const { t } = useTranslation();
    const [rowsPerPageFees, setRowsPerPageFees] = useState(3);
    const [currentFeesPage, setCurrentFeesPage] = useState(0);
    const [selectedFee, setSelectedFee] = useState(null);


    const handleFeesPageChange = (event, newPage) => {
        setCurrentFeesPage(newPage);
    };

    const slicedFees = fundData?.fees?.slice(
        currentFeesPage * rowsPerPageFees,
        currentFeesPage * rowsPerPageFees + rowsPerPageFees
    );

    const handleFeesRowsPerPage = (event) => {
        setRowsPerPageFees(parseInt(event.target.value, 3));
        setCurrentFeesPage(0);
    };


    return (
        <div className="fund-overview">
            <div className="glance-section">
                <div style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
                    <span className="titles" style={{ marginBottom: "5px" }}>{t('atAGlance')}</span>

                    <div style={{ display: "flex", height: "60%", flexDirection: "row", gap: "5px", alignItems: "center" }}>
                        <h4 className="titles" style={{ fontSize: "10px" }}>{`${t('currency')} ${fundData?.base_currency}`}</h4>
                        <span className="separator-v" />
                        <h4 className="titles soft" style={{ fontSize: "10px" }}>{`${t('figuresAsOf')} ${fundData?.eod_stats_xx?.time_ago_xx}`}</h4>
                    </div>
                </div>

                <hr className="separator-h" />
                <div className="stats-grid">
                    {/* ROW 1 */}
                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('class')}</h4>
                        <span className="titles">{fundData?.fund_type}</span>
                    </div>
                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('type')}</h4>
                        <span className="titles">{fundData?.fund_class_type}</span>
                    </div>
                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('inceptionDate')}</h4>
                        <span className="titles">{moment(fundData?.launch_date).format("DD-MMM-YYYY")}</span>
                    </div>
                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('totalFundDeposits')}</h4>
                        <span className="titles">{fundData?.fund_stats_xx?.gross_deposit_lc_short}</span>
                        <span className="titles soft" style={{ fontSize: "10px" }}>{`${fundData?.base_currency} ${numeral(fundData?.fund_stats_xx?.gross_deposit_lc).format("0,0.00")}`}</span>
                    </div>


                    {/* ROW 2 */}
                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('benchmark')}</h4>
                        <span className="titles">{fundData?.benchmark_name_xx}</span>
                    </div>
                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{`${t('target')} (%)`}</h4>
                        <span className="titles">{fundData?.benchmark_target}</span>
                    </div>
                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('status')}</h4>
                        <span className="titles">{fundData?.fund_status}</span>
                    </div>
                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('holdings')}</h4>
                        <span className="titles">{`${fundData?.base_currency} ${formatAmountsToShort(fundData?.eod_stats_xx?.holding_value_fc || 0, 2)}`}</span>
                        <span className="titles soft" style={{ fontSize: "10px" }}>{`${fundData?.base_currency} ${numeral(fundData?.eod_stats_xx?.holding_value_fc).format("0,0.00")}`}</span>
                    </div>


                    {/* ROW 3 */}
                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('investors')}</h4>
                        <span className="titles">{fundData?.fund_stats_xx?.investor_count_short}</span>
                        <span className="titles soft" style={{ fontSize: "10px" }}>{numeral(fundData?.fund_stats_xx?.investor_count).format("0,0")}</span>
                    </div>

                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('unitsIssued')}</h4>
                        <span className="titles">{fundData?.fund_stats_xx?.units_allocated_short}</span>
                        <span className="titles soft" style={{ fontSize: "10px" }}>{numeral(fundData?.fund_stats_xx?.units_allocated).format("0,0.00")}</span>
                    </div>

                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('accruedFees')}</h4>
                        <span className="titles">{fundData?.fund_stats_xx?.total_fees_fc_short}</span>
                        <span className="titles soft" style={{ fontSize: "10px" }}>{numeral(fundData?.fund_stats_xx?.total_fees_lc).format("0,0.00")}</span>
                    </div>

                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('netAssetValue')}</h4>
                        <span className="titles">{`${fundData?.base_currency} ${numeral((fundData?.eod_stats_xx?.nav_perunit_lc) || (fundData?.nav_per_unit || 0)).format("0,0.00")}`}</span>
                        <h4 className="titles soft" style={{ fontSize: "10px" }}>{`${t('asOf')} ${moment(fundData?.eod_stats_xx?.createdAt).format("DD-MMM-YYYY HH:mm")}`}</h4>
                    </div>

                    {/* ROW 4 */}
                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('netAssets')}</h4>
                        <span className="titles">{formatAmountsToShort(fundData?.eod_stats_xx?.net_assets_fc || 0)}</span>
                        <span className="titles soft" style={{ fontSize: "10px" }}>{numeral(fundData?.eod_stats_xx?.net_assets_fc || 0).format("0,0")}</span>
                    </div>

                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('liabilities')}</h4>
                        <span className="titles">{formatAmountsToShort(fundData?.eod_stats_xx?.liabilities_lc || 0)}</span>
                        <span className="titles soft" style={{ fontSize: "10px" }}>{numeral(fundData?.eod_stats_xx?.liabilities_lc || 0).format("0,0")}</span>
                    </div>

                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('availableCash')}</h4>
                        <span className="titles">{formatAmountsToShort(fundData?.eod_stats_xx?.outstanding_cash_lc || 0)}</span>
                        <span className="titles soft" style={{ fontSize: "10px" }}>{numeral(fundData?.eod_stats_xx?.outstanding_cash_lc || 0).format("0,0")}</span>
                    </div>
                    <div className="stat-card">
                        <h4 className="titles soft" style={{ fontSize: "14px" }}>{t('netReturn')}</h4>
                        <span className="titles">{`${fundData?.base_currency} ${numeral(fundData?.eod_stats_xx?.returns_lc || 0).format("0,0.00")}%`}</span>
                        <h4 className="titles soft" style={{ fontSize: "10px" }}>{`${t('asOf')} ${moment(fundData?.eod_stats_xx?.createdAt).format("DD-MMM-YYYY HH:mm")}`}</h4>
                    </div>
                </div>
            </div>

            <div className="entities-grid">
                {/* CUSTODIAN */}
                <div className="entity-card">
                    <div style={{ display: "flex", width: "100%", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                        <h4 className="titles org" style={{ alignSelf: "center" }}>{t('custodian').toUpperCase()}</h4>
                        <span className="icon-value-row">
                            <MdOutlineNumbers className="icon" /> <span className="sub-title">{fundData?.custodian_xx?.ent_code}</span>
                        </span>
                    </div>
                    <div className="entity-details">
                        <div className="entity-left">
                            {fundData?.custodian_xx?.ent_photo_url ?
                                <img src={fundData?.custodian_xx?.ent_photo_url} alt="" className="img" /> : <CiImageOn style={{ width: "60px", height: "60px" }} />}
                        </div>
                        <div className="entity-right">
                            <span className="main-title single-line"> {fundData?.custodian_xx?.ent_name || t('unspecified').toUpperCase()} </span>
                            <span className="icon-value-row" style={{ flex: "1.5" }}>
                                <MdEmail className="icon" /> <span className="sub-title">{fundData?.custodian_xx?.ent_email || ""}</span>
                            </span>
                            <span className="icon-value-row" style={{ flex: "1" }}>
                                <MdPhone className="icon" /> <span className="sub-title">{fundData?.custodian_xx?.ent_phone_no || ""}</span>
                            </span>
                        </div>
                    </div>
                </div>

                {/* TRUSTEE */}
                <div className="entity-card">
                    <div style={{ display: "flex", width: "100%", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                        <h4 className="titles org" style={{ alignSelf: "center" }}>{t('trustee').toUpperCase()}</h4>
                        <span className="icon-value-row">
                            <MdOutlineNumbers className="icon" /> <span className="sub-title">{fundData?.trustee_xx?.ent_code}</span>
                        </span>
                    </div>
                    <div className="entity-details">
                        <div className="entity-left">
                            {fundData?.trustee_xx?.ent_photo_url ?
                                <img src={fundData?.trustee_xx?.ent_photo_url} alt="" className="img" /> : <CiImageOn style={{ width: "60px", height: "60px" }} />}
                        </div>
                        <div className="entity-right">
                            <span className="main-title single-line"> {fundData?.trustee_xx?.ent_name || t('unspecified').toUpperCase()} </span>
                            <span className="icon-value-row" style={{ flex: "1.5" }}>
                                <MdEmail className="icon" /> <span className="sub-title">{fundData?.trustee_xx?.ent_email || ""}</span>
                            </span>
                            <span className="icon-value-row" style={{ flex: "1" }}>
                                <MdPhone className="icon" /> <span className="sub-title">{fundData?.trustee_xx?.ent_phone_no || ""}</span>
                            </span>

                        </div>
                    </div>
                </div>
            </div>
            <hr className="separator-h" />
            <div className="top-n">
                <div className="top-n-wrapper">
                    <div className="asset-allocation">
                        <div style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
                            <h4 className="titles org">{t('assetAllocation').toUpperCase()}</h4>
                            <span />
                        </div>
                        {fundData?.asset_allocation?.length > 0 ?
                            <div>
                                {fundData?.asset_allocation?.map((row, index) => {
                                    return (
                                        <div key={index} className="top-n-entity-wrapper">
                                            <div className="top-n-entity-left">
                                                <span className="sub-title xrsmall" style={{ width: "20px" }}>{index + 1}.</span>
                                                <span className="sub-title xrsmall" style={{ flex: "2" }}>{row?.asset_class_xx}</span>
                                            </div>
                                            <span className="percentage-bar-text" style={{ fontSize: "13px" }}>{row?.target_weight}%</span>
                                            <div className="tooltip">
                                                <span className="sub-title">{row?.asset_class_xx}</span>
                                                <hr className="separator-h" />
                                                <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{row?.asset_class_desc_xx}</div>
                                                <div style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
                                                    <div className="sub-title xrsmall" style={{ display: "flex", marginTop: "10px", flexDirection: "row", alignItems: "center", gap: "5px", flex: "1", alignSelf: "flex-end" }}>{`${t('target')} ${numeral(row?.target_weight).format("0,0.00")}%`}</div>
                                                    <div className="sub-title xrsmall" style={{ display: "flex", marginTop: "10px", flexDirection: "row", alignItems: "center", gap: "5px", flex: "1", alignSelf: "flex-end" }}>{`${t('min')} ${numeral(row?.min_weight).format("0,0.00")}%`}</div>
                                                    <div className="sub-title xrsmall" style={{ display: "flex", marginTop: "10px", flexDirection: "row", alignItems: "center", gap: "5px", flex: "1", alignSelf: "flex-end" }}>{`${t('max')} ${numeral(row?.max_weight).format("0,0.00")}%`}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            :
                            <h4 className="titles org" style={{ fontSize: "14px" }}>{t('noDataFound')}</h4>}
                    </div>
                    <div className="top-investors">
                        <div style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
                            <h4 className="titles org">{t('topNinvestors', { param1: fundData?.fund_stats_xx?.top_investors?.length || 0 }).toUpperCase()}</h4>
                            <h4 className="titles org">{fundData?.fund_stats_xx?.top_investors_short}</h4>
                        </div>
                        {fundData?.fund_stats_xx?.top_investors?.length > 0 ?
                            <div>
                                {fundData?.fund_stats_xx?.top_investors?.map((row, index) => {
                                    return (
                                        <div key={index} className="top-n-entity-wrapper">
                                            <div className="top-n-entity-left">
                                                {/* <IconComponent className="top-n-icon" /> */}
                                                <span className="sub-title xrsmall" style={{ width: "20px" }}>{index + 1}.</span>
                                                <span style={{ display: "flex", flexDirection: "row", width: "100%", alignItems: "center", justifyContent: "space-between", gap: "20px" }}>
                                                    <span className="sub-title xrsmall single-line" style={{ flex: "2" }}>{row?.investor_details_xx?.ent_name}</span>
                                                    <span className="sub-title xrsmall" style={{ flex: "1", textAlign: "right" }}>{formatAmountsToShort(row?.total_lc_deposit)}</span>
                                                </span>
                                            </div>
                                            <div className="top-n-entity-right">
                                                <div className="percentage-bar-container">
                                                    <div className="percentage-bar-fill" style={{ width: `${parseFloat(row?.percentage)}%` }} />
                                                    <span className="percentage-bar-text">{row?.percentage}%</span>
                                                </div>
                                            </div>
                                            <div className="tooltip">
                                                <div className="sub-title">{row?.investor_details_xx?.ent_name}</div>
                                                <hr className="separator-h" />
                                                <div className="sub-title xrsmall" style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", flex: "2" }}>{numeral(row?.total_lc_deposit).format("0,0.00")} : {row?.number_to_words_lc_xx}</div>
                                                <div className="sub-title xrsmall" style={{ display: "flex", marginTop: "10px", flexDirection: "row", alignItems: "center", gap: "5px", flex: "1", alignSelf: "flex-end" }}>{`${t('deposits')} : ${numeral(row?.deposit_count).format("0,0")}`}</div>
                                                <div className="sub-title xrsmall" style={{ display: "flex", marginTop: "10px", flexDirection: "row", alignItems: "center", gap: "5px", flex: "1", alignSelf: "flex-end" }}>{`${t('unitsAllocated')} : ${numeral(row?.units_allocated).format("0,0.00")}`}</div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                            :
                            <h4 className="titles org" style={{ fontSize: "14px" }}>{t('noDataFound')}</h4>}

                    </div>
                </div>
            </div>
            <ExpandableComponent
                title={t('fees').toUpperCase()}
                subtitle={""}
                defaultExpanded={fundData?.fees?.length > 0}
            >
                <div className="available-services">
                    <div className="table-content">
                        <TableContainer component={Paper} sx={{ border: 'none', boxShadow: 'none' }} className="table-container">
                            <Table sx={{ width: "max-content", border: 'none' }}>
                                <TableBody className="tablebody">
                                    {slicedFees?.map((row, index) => (
                                        <TableRow
                                            key={row?._id}
                                            className={`${selectedFee?._id === row?._id ? "tablerow current" : "tablerow"}`}
                                            onClick={() => {
                                                try {
                                                    setSelectedFee(row);
                                                } catch (error) {
                                                    console.log(error);
                                                }
                                            }}
                                        >
                                            <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                {currentFeesPage * rowsPerPageFees + index + 1}
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                <div style={{ display: "flex", flexDirection: "column", gap: "2px", width: "100%" }}>
                                                    <span className="sub-title">{row?.fee_name_xx}</span>
                                                    <span className="sub-title" style={{ fontSize: "10px" }}>{row?.fee_category_xx}</span>
                                                    <span className="sub-title soft" style={{ fontSize: "10px" }}>{row?.fee_code}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="table-cell" style={{ maxWidth: "650px" }}>
                                                <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "5px" }}>
                                                    <div style={{ display: "flex", flexDirection: "row", width: "100%", gap: "5px", alignItems: "center", justifyContent: "space-between" }}>
                                                        <div style={{ display: "flex", flexDirection: "column", flex: "1", gap: "5px" }}>
                                                            <span className="sub-title">{`${t('rate')} : ${row?.rate_type === "Fixed Amount" ? fundData?.base_currency : ""} ${numeral(row?.rate).format("0,0.00")} ${row?.rate_type === "Percentage" ? "%" : ""}`}</span>
                                                            <span className="sub-title">{`${t('freq')} : ${row?.rate_frequency}`}</span>
                                                        </div>
                                                        <div style={{ display: "flex", flexDirection: "column", flex: "1", gap: "5px" }}>
                                                            <span className="sub-title">{`${t('type')} : ${row?.rate_type}`}</span>
                                                            <span className="sub-title">{`${t('applyOn')} : ${row?.applicable_on}`}</span>
                                                        </div>
                                                    </div>
                                                    <span className="sub-title">{`${t('trigger')} : ${row?.trigger_condition}`}</span>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                rowsPerPageOptions={[3]}
                                rowsPerPage={rowsPerPageFees}
                                onRowsPerPageChange={handleFeesRowsPerPage}
                                component="div"
                                count={fundData?.fees?.length}
                                rows={fundData?.fees}
                                page={currentFeesPage}
                                onPageChange={handleFeesPageChange}
                            />
                        </TableContainer>
                    </div>
                </div>
            </ExpandableComponent>
        </div>
    )
}


export default FundOverview